import React, { useState } from 'react';
import {
  FaLaptop,
  FaDesktop,
  FaTv,
  FaBuilding,
  FaBarcode,
  FaMemory,
  FaHdd,
  FaUser,
  FaTag,
  FaMicrochip,
  FaSave,
  FaStickyNote,
  FaCalendarAlt
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import { getApiError } from '../utils/apiError';
import { Modal } from './common';

function AssetModal({ asset, onClose, onUpdate }) {
  const [notes, setNotes] = useState(asset?.notas || '');
  const [saving, setSaving] = useState(false);

  const getDeviceIcon = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'laptop':
        return <FaLaptop className="text-5xl text-indigo-500" />;
      case 'desktop':
        return <FaDesktop className="text-5xl text-sky-500" />;
      case 'aio':
        return <FaTv className="text-5xl text-violet-500" />;
      default:
        return <FaLaptop className="text-5xl text-gray-500" />;
    }
  };

  const getStatusBadge = (estado) => {
    const statusStyles = {
      disponible: 'bg-green-100 text-green-800',
      asignado: 'bg-sky-100 text-sky-800',
      reparacion: 'bg-amber-100 text-amber-800',
      'a desincorporar': 'bg-red-100 text-red-800',
      desincorporado: 'bg-red-100 text-red-800',
      baja: 'bg-red-100 text-red-800'
    };
    const style = statusStyles[estado?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase ${style}`}>
        {estado}
      </span>
    );
  };

  const handleSaveNotes = async () => {
    try {
      setSaving(true);
      await axiosInstance.patch(`/api/activos/${asset.id}`, {
        notas: notes
      });

      if (onUpdate) {
        onUpdate({ ...asset, notas: notes });
      }
    } catch (error) {
      console.error('Error al guardar las notas:', error);
      toast.error(getApiError(error, 'No se pudieron guardar las notas'));
    } finally {
      setSaving(false);
    }
  };

  // Componente reutilizable para los items del grid
  const DetailItem = ({ icon: Icon, label, children, className = "" }) => (
    <div className={`flex items-start gap-3 p-4 bg-gray-50 rounded-xl ${className}`}>
      <Icon className="text-sura-blue text-lg mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <strong className="block mb-1 text-gray-500 text-xs font-semibold uppercase tracking-wider">
          {label}
        </strong>
        {children}
      </div>
    </div>
  );

  return (
    <Modal isOpen={!!asset} onClose={onClose} size="xl">
      <Modal.Header onClose={onClose}>
        Detalle del Equipo
      </Modal.Header>
      <Modal.Body className="p-6">
        {/* ============================================
            HEADER: Icono + Info del Equipo + Estado
            ============================================ */}
        <div className="flex flex-col sm:flex-row gap-6 items-start pb-6 border-b-2 border-gray-100 mb-6">
          {/* Device Icon */}
          <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            {getDeviceIcon(asset.tipo)}
          </div>

          {/* Info Principal */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-400 font-mono">ID: {asset.id}</span>
            </div>
            <h2 className="m-0 text-gray-900 text-2xl font-bold mb-1">
              {asset.nombre_equipo || 'Equipo sin nombre'}
            </h2>
            <p className="m-0 text-gray-500 text-base">{asset.tipo}</p>
          </div>

          {/* Estado y Activo Fijo */}
          <div className="flex flex-col items-end gap-3">
            {getStatusBadge(asset.estado)}
            <span className="font-mono text-lg font-bold text-sura-blue bg-blue-50 px-4 py-2 rounded-lg">
              {asset.activo_fijo || 'Sin AF'}
            </span>
          </div>
        </div>

        {/* ============================================
            DETAILS GRID
            ============================================ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DetailItem icon={FaBuilding} label="Sede">
            <p className="m-0 text-gray-800 text-sm font-medium">{asset.sede || 'N/A'}</p>
          </DetailItem>

          <DetailItem icon={FaTag} label="Marca y Modelo">
            <p className="m-0 text-gray-800 text-sm font-bold">{asset.marca || 'N/A'}</p>
            <p className="m-0 text-gray-600 text-sm">{asset.modelo || 'N/A'}</p>
          </DetailItem>

          <DetailItem icon={FaBarcode} label="Serial">
            <p className="m-0 font-mono text-sm font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
              {asset.serial || 'N/A'}
            </p>
          </DetailItem>

          <DetailItem icon={FaMicrochip} label="Procesador">
            <p className="m-0 text-gray-800 text-sm">{asset.procesador || 'N/A'}</p>
          </DetailItem>

          <DetailItem icon={FaMemory} label="RAM">
            <p className="m-0 text-gray-800 text-sm font-medium">{asset.ram || 'N/A'}</p>
          </DetailItem>

          <DetailItem icon={FaHdd} label="Disco">
            <p className="m-0 text-gray-800 text-sm font-medium">{asset.disco || 'N/A'}</p>
          </DetailItem>

          <DetailItem icon={FaUser} label="Asignado a">
            {asset.empleado ? (
              <p className="m-0 text-gray-800 text-sm font-medium">{asset.empleado}</p>
            ) : asset.asignacion_manual ? (
              <p className="m-0 text-amber-700 text-sm font-medium">{asset.asignacion_manual}</p>
            ) : (
              <p className="m-0 text-gray-400 text-sm italic">No asignado</p>
            )}
          </DetailItem>

          {asset.fecha_asignacion && (
            <DetailItem icon={FaCalendarAlt} label="Fecha de Asignación">
              <p className="m-0 text-gray-800 text-sm font-medium">
                {new Date(asset.fecha_asignacion).toLocaleDateString('es-ES')}
              </p>
            </DetailItem>
          )}

          {/* Notas - Full Width */}
          <div className="sm:col-span-2 lg:col-span-3 flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
            <FaStickyNote className="text-sura-blue text-lg mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <strong className="block mb-2 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                Notas
              </strong>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-sura-blue focus:border-transparent"
                placeholder="Agregar notas sobre el equipo..."
                rows="3"
              />
              <button
                onClick={handleSaveNotes}
                disabled={saving}
                className="mt-2 px-4 py-2 text-sm rounded-lg bg-sura-blue text-white font-medium flex items-center gap-2 hover:bg-sura-blue-dark transition-colors disabled:opacity-50"
              >
                <FaSave /> {saving ? 'Guardando...' : 'Guardar Notas'}
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default AssetModal;